import { ROUTE_PERMISSIONS } from "../constants/permissions";
import { isSuperAdmin } from "./userRole";

function slugFromPermission(permission) {
  if (!permission) return "";
  if (typeof permission === "string") return permission.trim().toLowerCase();
  return String(permission.slug || permission.name || "")
    .trim()
    .toLowerCase();
}

function normalizeRole(role) {
  if (!role || typeof role !== "object") return null;
  const permissions = Array.isArray(role.permissions)
    ? role.permissions.map((p) => ({
        id: p?.id,
        name: p?.name || "",
        slug: slugFromPermission(p),
        group: p?.group || "",
      }))
    : [];

  return {
    id: role.id,
    name: role.name || "",
    slug: role.slug || "",
    permissions,
  };
}

/** Collect unique permission objects from user + roles. */
export function extractPermissionsList(user) {
  if (!user) return [];

  const bySlug = new Map();

  const add = (permission) => {
    const slug = slugFromPermission(permission);
    if (!slug || bySlug.has(slug)) return;
    bySlug.set(slug, {
      id: permission?.id,
      name: permission?.name || slug,
      slug,
      group: permission?.group || "",
    });
  };

  (user.permissions || []).forEach(add);

  (user.roles || []).forEach((role) => {
    const normalized = typeof role === "object" ? role : null;
    (normalized?.permissions || []).forEach(add);
  });

  return Array.from(bySlug.values());
}

export function extractRolesList(user) {
  if (!user?.roles || !Array.isArray(user.roles)) return [];
  return user.roles.map(normalizeRole).filter(Boolean);
}

export function extractPermissionSlugs(user) {
  return extractPermissionsList(user).map((p) => p.slug);
}

/** Normalize user from login / profile for Redux. */
export function normalizeAuthUser(user) {
  if (!user) return null;

  const roles = extractRolesList(user);
  const permissions = extractPermissionsList(user);
  const permissionSlugs = permissions.map((p) => p.slug);

  return {
    ...user,
    roles,
    permissions,
    permissionSlugs,
  };
}

export function hasPermission(user, permissionSlug) {
  if (!permissionSlug) return true;
  if (isSuperAdmin(user)) return true;

  const slug = String(permissionSlug).trim().toLowerCase();
  const slugs = user?.permissionSlugs || extractPermissionSlugs(user);

  return slugs.includes(slug);
}

export function hasAnyPermission(user, permissionSlugs = []) {
  if (!permissionSlugs?.length) return true;
  if (isSuperAdmin(user)) return true;
  return permissionSlugs.some((slug) => hasPermission(user, slug));
}

export function hasAllPermissions(user, permissionSlugs = []) {
  if (!permissionSlugs?.length) return true;
  if (isSuperAdmin(user)) return true;
  return permissionSlugs.every((slug) => hasPermission(user, slug));
}

export function getModulePermissions(user, module) {
  const prefix = `${module}.`;
  return {
    canCreate: hasPermission(user, `${prefix}create`),
    canRead: hasPermission(user, `${prefix}read`),
    canUpdate: hasPermission(user, `${prefix}update`),
    canDelete: hasPermission(user, `${prefix}delete`),
  };
}

export function canAccessRoute(user, pathname) {
  const path = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  const required = ROUTE_PERMISSIONS[path];

  if (required === null || required === undefined) {
    return true;
  }

  return hasPermission(user, required);
}
